/**
 * IdleLogoutController — signs the user out after ONE HOUR of no
 * interaction (SCRUM-142, owner decision 2026-07-12: idle timeout yes;
 * logout-on-browser-close no; cross-tab/single-tab enforcement no — see
 * the ticket for the full policy discussion. Window raised 30min -> 1h on
 * 2026-08-13: users were being signed out mid-task, and the timeout must be
 * identical for password and Google sign-in — it is enforced here, client
 * side, so it already is).
 *
 * Renders nothing (same "controller" shape as `ThemeController`, mounted
 * once near the provider-tree root in main.tsx). While a session is
 * active, it listens for real user interaction (pointer, keyboard,
 * scroll, touch) on `window` and resets the one-hour timer on every one,
 * throttled to at most once/second so a held-down key or a drag doesn't
 * thrash `setTimeout` churn. When the timer fires, it calls the SAME
 * `logout()` helper the Settings screen uses (SCRUM-138: revokes the
 * refresh-cookie session server-side, clears the in-memory token, clears
 * the TanStack Query cache) and shows a toast explaining why. No manual
 * navigation is needed — `RequireAuth` reacts to `isAuthenticated`
 * flipping false and redirects on its own on any protected route.
 *
 * Idle is measured across the whole browser profile, not per tab, via a
 * `lastActivityAt` timestamp in `localStorage`. The original per-tab,
 * in-memory version had two holes, both reproduced in a real browser:
 *
 *   1. A reload restarted the idle clock. With no persisted timestamp and
 *      no server-side idle expiry (the refresh cookie is pinned to 30
 *      days), a long gap with one reload in the middle left the user
 *      signed in — the idle rule was trivially defeated.
 *   2. An idle background tab logged out a user who was actively working
 *      in another tab. Tabs share one refresh cookie, so the idle tab's
 *      logout revoked the session for BOTH, and the active tab kept
 *      rendering until its access token expired.
 *
 * Both are fixed by the shared timestamp: a mount resumes the REMAINING
 * window rather than starting a fresh one, and the deadline re-reads the
 * shared value before signing out, re-arming instead if any tab has been
 * active. Activity is recorded even while signed out, so the sign-in
 * interaction itself counts and a fresh login is never tripped by a stale
 * timestamp left behind by an earlier session.
 *
 * Only a timestamp is stored — never the token. The `localStorage` ban in
 * the guardrails is specifically about JWTs (REQ-PRD-§11.2-A); the access
 * token still lives in JS memory only.
 *
 * Still deliberately NOT logout-on-browser-close and NOT cross-tab session
 * enforcement — both were explicitly declined on the ticket. (An idle
 * timestamp does mean a browser reopened after >1 hour away lands on
 * an expired session, which is the idle rule doing its job, not a
 * close-triggered logout.)
 */
import { useEffect, useRef } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { logout } from "@/features/settings/api";
import { selectIsAuthenticated, useAuthStore } from "@/lib/auth-store";

/**
 * One hour, per the owner's 2026-08-13 decision (was 30 minutes).
 *
 * This is the ONLY idle clock in the product, so it applies identically to
 * password and Google sign-in. It is not the same thing as token lifetime:
 * the access token still expires every 15 minutes and is renewed silently
 * (REQ-PRD-§11.2-A), and the refresh cookie still lasts 30 days. What the
 * user experiences as "the session" is this window.
 */
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

/** Minimum gap between timer resets — activity events fire far more often
 * than the timeout needs to react to; this just bounds the churn. */
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS: readonly (keyof WindowEventMap)[] = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
];

/**
 * Shared across every tab of this origin. A timestamp only — never a
 * token (see the module docstring on REQ-PRD-§11.2-A).
 */
export const LAST_ACTIVITY_STORAGE_KEY = "garden.auth.lastActivityAt";

/** Last activity in epoch ms, or null when absent/unreadable/corrupt. */
function readLastActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private mode / storage disabled — degrade to the previous per-tab
    // behaviour rather than breaking the app.
    return null;
  }
}

function writeLastActivity(atMs: number): void {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(atMs));
  } catch {
    // Ignore — see readLastActivity.
  }
}

export function IdleLogoutController(): ReactElement | null {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const { t } = useTranslation("common");
  // Ref so the toast copy inside the timeout callback is always current
  // without re-subscribing the activity listeners on every language switch.
  const idleMessageRef = useRef(t("session.idleLogout"));
  idleMessageRef.current = t("session.idleLogout");

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let lastWrite = 0;

    /** Arm the deadline for whatever remains of the window after `since`. */
    const armFrom = (since: number): void => {
      clearTimeout(timeoutId);
      // Activity is still recorded while signed out (so the sign-in
      // interaction counts), but there is nothing to sign out of yet.
      if (!isAuthenticated) return;
      const remaining = IDLE_TIMEOUT_MS - (Date.now() - since);
      timeoutId = setTimeout(onDeadline, Math.max(0, remaining));
    };

    const onDeadline = (): void => {
      // Re-read the SHARED timestamp: another tab may have been active
      // while this one sat untouched. Only sign out if the whole browser
      // has genuinely been idle.
      const last = readLastActivity();
      if (last !== null && Date.now() - last < IDLE_TIMEOUT_MS) {
        armFrom(last);
        return;
      }
      void logout()
        .then(() => {
          toast.info(idleMessageRef.current);
        })
        .catch(() => undefined);
    };

    const onActivity = (): void => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastWrite = now;
      writeLastActivity(now);
      armFrom(now);
    };

    // Resume the existing window rather than starting a fresh one — this
    // is what makes a reload (or a restored tab) keep counting.
    const stored = readLastActivity();
    if (stored === null) {
      const now = Date.now();
      lastWrite = now;
      writeLastActivity(now);
      armFrom(now);
    } else {
      // Already past the deadline → armFrom schedules at 0 and onDeadline
      // signs out on the next tick (never synchronously during render).
      armFrom(stored);
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    return () => {
      clearTimeout(timeoutId);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
    // isAuthenticated alone: the listeners are identical either way; the
    // flip only decides whether a deadline is armed.
  }, [isAuthenticated]);

  return null;
}

export default IdleLogoutController;
